import { manageCacheSize } from '@/utils/cache-utils';
import { useEffect, useRef } from 'react';
import { useExperienceCache, useSeatsCache } from './useCache';

interface CacheManagerOptions {
  cleanupInterval?: number;
  maxExperienceCacheSizeKB?: number;
  maxSeatsCacheSizeKB?: number;
}

export function useCacheManager(options?: CacheManagerOptions) {
  const {
    cleanupInterval = 5 * 60 * 1000, // 5 minutes
    maxExperienceCacheSizeKB = 2048, // 2MB
    maxSeatsCacheSizeKB = 1024, // 1MB
  } = options || {};

  const experienceCache = useExperienceCache();
  const seatsCache = useSeatsCache();
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const performCleanup = () => {
    experienceCache.cleanupExpired();
    seatsCache.cleanupExpired();

    manageCacheSize('masp_tickets_', maxExperienceCacheSizeKB);
    manageCacheSize('seats_cache_', maxSeatsCacheSizeKB);

    const experienceStats = experienceCache.getCacheSize();
    const seatsStats = seatsCache.getCacheSize();

    console.log('Cache cleanup performed:', {
      experience: experienceStats,
      seats: seatsStats,
      timestamp: new Date().toISOString(),
    });
  };

  useEffect(() => {
    performCleanup();

    cleanupIntervalRef.current = setInterval(performCleanup, cleanupInterval);

    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, [cleanupInterval, maxExperienceCacheSizeKB, maxSeatsCacheSizeKB]);

  return {
    experienceCache,
    seatsCache,
    performCleanup,
  };
}
